import React, { useLayoutEffect, useState } from "react";
import type { LiveRoomInfo } from "../../../shared/game-preview/LiveRoomPreview";
import { flagUrl } from "../../../shared/flags";
import { asset } from "../../../shared/portal-env";
import Daily from "../../../shared/progress-ui/Daily";
import { useProgress } from "../../../shared/progress";
import { LevelRing } from "../../../shared/progress-ui/LevelBadge";
import { LiveWindow, LiveFoot, RoyClock, RoySeats } from "./parts";
import { mmss } from "./hooks";
import { Bars, Chev, Crown, Doors, Grid } from "./icons";
import s from "./FrameHome.module.scss";

// The start screen in a game site's frame on a computer (the designs on trial,
// /debug/portal-lab): the frame is 16:9 and small (CrazyGames: 907 x 510 up to
// 1216 x 684, and their full screen), so the screen is drawn on a 960 x 540
// stage scaled to fit it. The live game is drawn at the frame's real size
// (a canvas does not scale well), placed where the stage wants it.
//  A  the phone's screen made for a mouse: LET'S GO a wide button bottom right,
//     Royale and Rooms beside it
//  B  a game menu: a column on the left (the name, LET'S GO, Royale, Rooms,
//     Leaderboard, you at its foot), the live game beside it
//  C  the website's page in small: the live window with LET'S GO under it,
//     Royale, Rooms and your level in a column on the right
export type FrameLook = "a" | "b" | "c";

export interface FrameHomeProps {
  look: FrameLook;
  me: { name: string; flag: string };
  royale: { secs: number | null; pct: number };
  onInfo: (info: LiveRoomInfo | null) => void;
  info: LiveRoomInfo | null;
  onPlay: () => void;
  onRoyale: () => void;
  onRooms: () => void;
  onMenu: () => void;
  onPlayer: () => void;
  onDaily: () => void;
  onLeaderboard: () => void;
  notice?: React.ReactNode; // the note on our terms (a game site's first visit)
}

const W = 960, H = 540;
// Where the stage puts the live game (stage px), by look; null: behind it all.
const LIVE: Record<FrameLook, { x: number; y: number; w: number; h: number } | null> = {
  a: null,
  b: { x: 300, y: 0, w: 660, h: 540 },
  c: { x: 24, y: 64, w: 600, h: 352 },
};

function useStage() {
  const [k, setK] = useState({ k: 1, ox: 0, oy: 0 });
  useLayoutEffect(() => {
    const fit = () => {
      const vw = window.innerWidth, vh = window.innerHeight;
      const kk = Math.min(vw / W, vh / H);
      setK({ k: kk, ox: (vw - W * kk) / 2, oy: (vh - H * kk) / 2 });
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, []);
  return k;
}

const Live = () => <span className={s.live}><i />LIVE</span>;

function Me({ me, onPlayer, ring }: { me: FrameHomeProps["me"]; onPlayer: () => void; ring?: boolean }) {
  const p = useProgress();
  return (
    <button type="button" className={s.me} onClick={onPlayer} aria-label="Player">
      <img src={flagUrl(me.flag)} alt="" />
      <b>{me.name || "Guest"}</b>
      {ring && <span className={s.meRing}><LevelRing level={p.level} into={p.into} need={p.need} size={30} stroke={3} label={false} /></span>}
    </button>
  );
}

function RoyaleButton({ royale, onClick, wide }: { royale: FrameHomeProps["royale"]; onClick: () => void; wide?: boolean }) {
  return (
    <button type="button" className={`${s.roy} ${wide ? s.royWide : ""}`} onClick={onClick} aria-label="Royale" data-sound="confirm">
      <span className={s.royTop}><Crown /><b>ROYALE</b><span className={s.royClock}>{wide ? <RoyClock secs={royale.secs} /> : mmss(royale.secs)}</span></span>
      {wide && <RoySeats pct={royale.pct} />}
    </button>
  );
}

export default function FrameHome(props: FrameHomeProps) {
  const { look, me, royale, onInfo, info, onPlay, onRoyale, onRooms, onMenu, onPlayer, onDaily, onLeaderboard, notice } = props;
  const { k, ox, oy } = useStage();
  const live = LIVE[look];
  const liveStyle: React.CSSProperties = live
    ? { left: ox + live.x * k, top: oy + live.y * k, width: live.w * k, height: live.h * k }
    : { inset: 0 };
  return (
    <div className={`${s.root} ${s[look]}`}>
      <div className={s.backdrop} aria-hidden="true" />
      <div className={`${s.liveBox} ${live ? s.liveFramed : ""}`} style={liveStyle}><LiveWindow onInfo={onInfo} /></div>
      {look === "a" && <div className={s.shadeA} aria-hidden="true" />}
      <div className={s.stage} style={{ transform: `translate(${ox}px, ${oy}px) scale(${k})` }}>
        {look === "a" && (<>
          <div className={s.aTop}><Me me={me} onPlayer={onPlayer} /></div>
          <div className={s.aDaily}><Daily panel onOpen={onDaily} /></div>
          {notice && <div className={s.aNotice}>{notice}</div>}
          <button type="button" className={s.iconBtn} style={{ right: 20, top: 16 }} onClick={onMenu} aria-label="Menu"><Grid /></button>
          <div className={s.aFoot}><LiveFoot info={info} className={s.foot} lead={<Live />} /></div>
          <div className={s.aCluster}>
            <button type="button" className={s.play} onClick={onPlay} aria-label="Play Now" data-sound="confirm"><b>LET'S GO!</b></button>
            <div className={s.aRow}>
              <RoyaleButton royale={royale} onClick={onRoyale} />
              <button type="button" className={s.side} onClick={onRooms} aria-label="Game Rooms"><Doors /><b>Rooms</b></button>
            </div>
          </div>
        </>)}

        {look === "b" && (<>
          <nav className={s.bCol}>
            <div className={s.brand}><img src={asset("/logo-mark.svg")} alt="" /><b>ACHTUNG<span>·</span>ROYALE</b></div>
            <button type="button" className={`${s.play} ${s.bPlay}`} onClick={onPlay} aria-label="Play Now" data-sound="confirm"><b>LET'S GO!</b></button>
            <RoyaleButton royale={royale} onClick={onRoyale} wide />
            <button type="button" className={s.row} onClick={onRooms}><Doors /><b>Game Rooms</b><Chev className={s.chev} /></button>
            <button type="button" className={s.row} onClick={onLeaderboard}><Bars /><b>Leaderboard</b><Chev className={s.chev} /></button>
            <div className={s.bFoot}>
              <Me me={me} onPlayer={onPlayer} ring />
              <button type="button" className={s.iconBtn} style={{ position: "static" }} onClick={onMenu} aria-label="Menu"><Grid /></button>
            </div>
          </nav>
          <div className={s.bDaily}><Daily panel onOpen={onDaily} /></div>
          {notice && <div className={s.bNotice}>{notice}</div>}
          <div className={s.bFootLive}><LiveFoot info={info} className={s.foot} lead={<Live />} /></div>
        </>)}

        {look === "c" && (<>
          <header className={s.cBar}>
            <div className={s.brand}><img src={asset("/logo-mark.svg")} alt="" /><b>ACHTUNG<span>·</span>ROYALE</b></div>
            {notice && <div className={s.cNotice}>{notice}</div>}
            <div className={s.cBarR}>
              <Me me={me} onPlayer={onPlayer} />
              <button type="button" className={s.iconBtn} style={{ position: "static" }} onClick={onMenu} aria-label="Menu"><Grid /></button>
            </div>
          </header>
          <div className={s.cLiveFoot}><LiveFoot info={info} className={s.foot} lead={<Live />} /></div>
          <button type="button" className={`${s.play} ${s.cPlay}`} onClick={onPlay} aria-label="Play Now" data-sound="confirm"><b>LET'S GO!</b></button>
          <div className={s.cCol}>
            <RoyaleButton royale={royale} onClick={onRoyale} wide />
            <button type="button" className={s.row} onClick={onRooms}><Doors /><b>Game Rooms</b><Chev className={s.chev} /></button>
            <div className={s.cDaily}><Daily panel onOpen={onDaily} /></div>
          </div>
        </>)}
      </div>
    </div>
  );
}
